import snakecase from 'lodash.snakecase'
import { generateFieldsByTableColumns } from '@/utils/functions'

/**
 * ============================================================================
 * PARTS-SPECIFIC HELPERS
 * Business logic specific to parts module
 * ============================================================================
 */

/**
 * Valid values for the backend PartSort.column / PartColumn enum.
 * @see recar-backend/recar/managers/part/utils/__init__.py
 */
export const PART_SORT_COLUMNS = [
	'id',
	'category',
	'price',
	'sell_price',
	'quantity',
	'location',
	'department',
	'created_at',
	'updated_at',
	'input_user',
	'status',
	'order_id',
	'handover'
]

/**
 * Apply partnership logic to parts query payload
 * @param {Object} payload - Base payload
 * @param {Object} rootState - Vuex root state
 * @param {string} showing - Showing mode ('all', 'department', etc.)
 * @returns {Object} Payload with partnership logic applied
 */
export function applyPartnershipLogic (payload, rootState, showing) {
	const user = rootState?.Auth?.user

	if (!user?.selectedDepartmentId || !user?.admin) {
		delete payload.departmentIds
	}

	if (user?.selectedDepartmentId &&
        (payload.ebayStatuses || showing === 'department')) {
		payload.departmentIds = user.selectedDepartmentId
	}

	if (showing === 'all' &&
        user?.hasPartnership &&
        user?.selectedDepartmentId) {
		const partnerships = user.partnerships || []
		const departmentIds = partnerships.map(role => role.departmentId)
		const departmentsSet = new Set(departmentIds)
		departmentsSet.delete(user.selectedDepartmentId)
		payload.partnership = true
		payload.departmentIds = Array.from(departmentsSet)
	} else {
		payload.partnership = false
	}

	return payload
}

/**
 * Build comprehensive parts query with all filters
 * @param {Object} options
 * @param {Object} options.state - Module state
 * @param {Object} options.rootState - Root state
 * @param {Object} options.data - Query parameters
 * @param {Array} options.defaultStatuses - Default status filters
 * @returns {Object} Complete query configuration
 */
export function buildPartsQueryPayload ({
	state,
	rootState,
	data = {},
	defaultStatuses = ['in_stock', 'reserved', 'not_parsed', 'out_of_stock']
}) {
	const {
		nearestParentId = null,
		page,
		size,
		isRootsChild = true,
		hasPrice = null,
		isSaleAllowed = null,
		defaultQuery = true,
		skipCachedFilters = false,
		..._payload
	} = data

	const payload = { ..._payload }

	// Handle nearestParentId special case
	if (nearestParentId) {
		return {
			payload: { nearestParentId },
			page: 1,
			size: 999
		}
	}

	// Handle cached filters
	if (Object.keys(state.searchFilters || {}).length) {
		const {
			page: _page,
			size: _size,
			nearestParentId: _nearestParentId,
			hasPrice: _hasPrice,
			isSaleAllowed: _isSaleAllowed,
			statuses: _statuses,
			...cachedPayload
		} = state.searchFilters

		if (_nearestParentId !== false) {
			payload.nearestParentId = _nearestParentId
		}

		if (_hasPrice !== null) {
			payload.hasPrice = _hasPrice
		}

		if (_isSaleAllowed !== undefined) {
			payload.isSaleAllowed = _isSaleAllowed
		} else {
			payload.isSaleAllowed = true
		}

		payload.statuses = _statuses || defaultStatuses

		if (cachedPayload.ids) {
			payload.statuses = _statuses || null
		}

		if (!skipCachedFilters) {
			Object.assign(payload, cachedPayload)
		}

		return {
			payload: applyPartnershipLogic(payload, rootState, state.showing),
			page: page || _page,
			size: size || _size
		}
	}

	// Handle direct params
	if (nearestParentId !== false) {
		payload.nearestParentId = nearestParentId
	}
	if (hasPrice !== null) {
		payload.hasPrice = hasPrice
	}
	if (isSaleAllowed !== null) {
		payload.isSaleAllowed = isSaleAllowed
	}

	payload.statuses = defaultStatuses
	payload.isRootsChild = isRootsChild
	payload.defaultQuery = defaultQuery

	return {
		payload: applyPartnershipLogic(payload, rootState, state.showing),
		page,
		size
	}
}

/**
 * Build sort configuration for parts
 * @param {Object} tableState - Table state
 * @param {Object} rootState - Root state
 * @param {string} showing - Showing mode
 * @returns {Object} Sort configuration
 */
export function buildPartsSort (tableState, rootState, showing) {
	const sort = {
		column: tableState.sortBy ? snakecase(tableState.sortBy) : 'id',
		order: tableState.sortOrder
	}

	const departmentId = rootState?.Auth?.user?.selectedDepartmentId
	if (sort.column === 'location' && (!departmentId || showing === 'all')) {
		sort.column = 'department'
	}

	if (!PART_SORT_COLUMNS.includes(sort.column)) {
		sort.column = 'id'
	}

	return sort
}

/**
 * Build columns configuration for parts
 * @param {Array} tableColumns - Table columns
 * @param {Array} originalColumns - Original columns config
 * @returns {Object} Columns configuration
 */
export function buildPartsColumns (tableColumns, originalColumns) {
	const fallback = {
		showLocation: false,
		showTools: false,
		showStatus: false,
		showPrice: false,
		showSuggestedPrice: false,
		showDemand: false,
		showCost: false,
		showOem: false,
		showPhoto: false,
		showParams: false,
		showTitle: false,
		showInputUser: false,
		showCreatedAt: false,
		showUpdatedAt: false,
		showSellPrice: false,
		showProfit: false,
		showMargin: false,
		showQuantity: false,
		showSku: false,
		showGroupedSale: false,
		showGroupedSaleComment: false,
		showAccountingCode: false,
		showIsSaleAllowed: false
	}
	return {
		...fallback,
		...generateFieldsByTableColumns(tableColumns, originalColumns)
	}
}

/**
 * Process part nearestChildren
 * @param {Object} part - Part data
 * @returns {Array} Nearest children
 */
export function processNearestChildren (part) {
	return part.nearestChildren ||
        part.children?.filter(child =>
        	child.nearestParentId === part.id && child.id !== part.id
        ) || []
}

/**
 * Clean payload for related parts query
 * @param {Object} payload - Query payload
 * @param {Object} options - Cleaning options
 * @returns {Object} Cleaned payload
 */
export function cleanRelatedPartsPayload (payload, { alternativeOemCode, modificationFitments }) {
	const cleaned = { ...payload }

	// Remove fields not needed for related parts
	delete cleaned.codes
	delete cleaned.wildcardOemCodes
	delete cleaned.manufacturerIds
	delete cleaned.modelIds
	delete cleaned.modificationIds

	// Add related parts specific fields
	cleaned.alternativeOemCode = alternativeOemCode
	cleaned.modificationFitments = modificationFitments

	if (modificationFitments) {
		delete cleaned.categoryIds
	}

	return cleaned
}
